/**
 * Settings Components for PhotoCollection2 Test Page
 *
 * Control panel sections for configuring test page behavior.
 */

import { CollectionControlPanel, type DetailLocation } from '../../components/composed/CollectionControlPanel';
import { Text } from '../../components/atoms/Text';
import type { TemplateName } from '../../components/PhotoItem2/PhotoItem2.types';
import type { LayoutName, SelectionMode } from '../../components/PhotoCollection2/PhotoCollection2.types';
import type { Photo } from '../../types/photo';

/**
 * Sort criterion for collection ordering
 */
export type SortCriterion = 'none' | 'date-asc' | 'date-desc' | 'title-asc' | 'title-z';

/**
 * CollectionSettings - Control panel section for PhotoCollection2 settings
 */
export interface CollectionSettingsProps {
  layout: LayoutName;
  itemTemplate: TemplateName;
  selectionMode: SelectionMode;
  zoom: number;
  detailLocation: DetailLocation;
  reorderMode: boolean;
  activeSortCriterion: SortCriterion;
  // Header/Footer slot controls (#102)
  showFixedHeader: boolean;
  showScrollHeader: boolean;
  showScrollFooter: boolean;
  showFixedFooter: boolean;
  onLayoutChange: (layout: LayoutName) => void;
  onItemTemplateChange: (template: TemplateName) => void;
  onSelectionModeChange: (mode: SelectionMode) => void;
  onZoomChange: (zoom: number) => void;
  onDetailLocationChange: (location: DetailLocation) => void;
  onReorderModeChange: (enabled: boolean) => void;
  onSortCriterionChange: (criterion: SortCriterion) => void;
  onShowFixedHeaderChange: (show: boolean) => void;
  onShowScrollHeaderChange: (show: boolean) => void;
  onShowScrollFooterChange: (show: boolean) => void;
  onShowFixedFooterChange: (show: boolean) => void;
}

export function CollectionSettings({
  layout,
  itemTemplate,
  selectionMode,
  zoom,
  detailLocation,
  reorderMode,
  activeSortCriterion,
  showFixedHeader,
  showScrollHeader,
  showScrollFooter,
  showFixedFooter,
  onLayoutChange,
  onItemTemplateChange,
  onSelectionModeChange,
  onZoomChange,
  onDetailLocationChange,
  onReorderModeChange,
  onSortCriterionChange,
  onShowFixedHeaderChange,
  onShowScrollHeaderChange,
  onShowScrollFooterChange,
  onShowFixedFooterChange,
}: CollectionSettingsProps) {
  return (
    <div className="space-y-4">
      <CollectionControlPanel
        layout={layout}
        itemTemplate={itemTemplate}
        selectionMode={selectionMode}
        zoom={zoom}
        detailLocation={detailLocation}
        onLayoutChange={onLayoutChange}
        onItemTemplateChange={onItemTemplateChange}
        onSelectionModeChange={onSelectionModeChange}
        onZoomChange={onZoomChange}
        onDetailLocationChange={onDetailLocationChange}
      />

      {/* Sort & Reorder Controls */}
      <div>
        <Text className="text-sm font-medium mb-2">Sort & Reorder</Text>
        <div className="space-y-3">
          <div>
            <Text className="text-xs text-muted mb-1.5">Sort by:</Text>
            <select
              value={activeSortCriterion}
              onChange={(e) => onSortCriterionChange(e.target.value as SortCriterion)}
              className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
            >
              <option value="none">Manual Order</option>
              <option value="date-asc">Date (Oldest)</option>
              <option value="date-desc">Date (Newest)</option>
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-z">Title (Z-A)</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={reorderMode}
              onChange={(e) => onReorderModeChange(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <Text className="text-sm">Enable Reorder Mode</Text>
          </label>
        </div>
      </div>

      {/* Header & Footer Slots (#102) */}
      <div>
        <Text className="text-sm font-medium mb-2">Header & Footer Slots</Text>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showFixedHeader}
              onChange={(e) => onShowFixedHeaderChange(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <Text className="text-sm">Fixed Header</Text>
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">outside scroll</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showScrollHeader}
              onChange={(e) => onShowScrollHeaderChange(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <Text className="text-sm">Scroll Header</Text>
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">inside scroll</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showScrollFooter}
              onChange={(e) => onShowScrollFooterChange(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <Text className="text-sm">Scroll Footer</Text>
            <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">inside scroll</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showFixedFooter}
              onChange={(e) => onShowFixedFooterChange(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary"
            />
            <Text className="text-sm">Fixed Footer</Text>
            <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">outside scroll</span>
          </label>

          <Text className="text-xs text-muted mt-2">
            Scroll slots supported: grid, magazine, vertical. Fixed slots work on all layouts.
          </Text>
        </div>
      </div>
    </div>
  );
}

/**
 * PhotoViewerSettings - Control panel section for PhotoViewer settings
 */
export interface PhotoViewerSettingsProps {
  photos: Photo[];
  selectedIndex: number;
  onIndexChange: (index: number) => void;
}

export function PhotoViewerSettings({
  photos,
  selectedIndex,
  onIndexChange,
}: PhotoViewerSettingsProps) {
  const safeIndex = Math.min(selectedIndex, Math.max(0, photos.length - 1));

  if (photos.length === 0) {
    return (
      <Text className="text-sm text-muted">
        No photos available. Switch to Real data source.
      </Text>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Text className="text-sm font-medium mb-2">Photo Selection</Text>
        <select
          key={photos.length > 0 ? photos[0].id : 'empty'}
          value={safeIndex}
          onChange={(e) => onIndexChange(parseInt(e.target.value))}
          className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
        >
          {photos.map((photo, index) => {
            const displayName = photo.description || photo.title || photo.id;
            return (
              <option key={photo.id} value={index}>
                Photo {index + 1} - {displayName}
              </option>
            );
          })}
        </select>
      </div>

      <div>
        <Text className="text-sm font-medium mb-2">Instructions</Text>
        <div className="text-xs text-muted space-y-1">
          <div>• Pinch to zoom (100%-400%)</div>
          <div>• Drag to pan when zoomed</div>
          <div>• Double-click to toggle zoom</div>
          <div>• Ctrl+Scroll for mouse wheel zoom</div>
        </div>
      </div>
    </div>
  );
}
